/**
 * CAP-TAX-1 · Platform Capability Taxonomy (Tier 0)
 *
 * Codifies KPAP-1 (adopted 2026-04-23). Every capability domain in Kairos
 * expresses licensing and feature gating through flags in this frozen
 * namespace. Adding a namespace requires a new CAP-TAX-N session — NOT an
 * ad-hoc edit to this file.
 *
 * Frozen namespaces (20) — post PILLAR-KSCHED-1 + PILLAR-KSALES-OPS-1 + PILLAR-KSTRAT-1 (2026-05-01):
 *   cx, comms, kpol, kfol, kiol, exec, aegis, sentinel, sandbox, idp, integrations, counsel,
 *   embedding, forecast, kbrain, customer, kfin, ksched, ksales-ops, kstrat
 *
 * `platform` was retired in CAPABILITY-GATE-ERADICATION-1. Module-enable
 * checks moved to `tenants/{tid}.config.modules.enabled.<module>` (consumed
 * via `useModuleEnabled` in src/services/CapabilityService.js); the only
 * remaining platform-shaped feature flag (UCO reference impl) was migrated
 * to `aegis.uco_reference_impl`. `counsel` was admitted as the twelfth namespace.
 * `embedding`, `forecast`, `kbrain` admitted in CAP-TAX-3 (Wave KB-A, EMBED-1, 2026-04-29).
 * `customer` admitted in PILLAR-KCX-1 (2026-04-30) — Customer Experience Pillar.
 * `kfin` admitted in PILLAR-KFIN-1 (2026-04-30) — Financial Pillar substrate.
 * `ksales-ops` admitted in PILLAR-KSALES-OPS-1 (2026-05-01) — Sales Operations Pillar (R3).
 * `kstrat` admitted in PILLAR-KSTRAT-1 (2026-05-01) — Strategy Pillar (R3).
 * CAP-TAX-1 BINDING: kfin-tier checks use config.modules.enabled.kfin.<feature>.
 */
import { z } from 'zod';

export const CAPABILITY_NAMESPACES = [
  'cx',
  'comms',
  'kpol',
  'kfol',
  'kiol',
  'exec',
  'aegis',
  'sentinel',
  'sandbox',
  'idp',
  'integrations',
  'counsel',
  // CAP-TAX-3 (EMBED-1, 2026-04-29) — Wave KB-A substrate namespaces
  'embedding',  // Voyage-3 embedding substrate (EMBED-1)
  'forecast',   // Forecast substrate (TS-1, pending)
  'kbrain',     // KBRAIN observation/audit substrate (LOG-1, pending)
  // PILLAR-KCX-1 (2026-04-30) — Customer Experience Pillar (FROZEN)
  // Flags: customer.shipperPortal, customer.whiteLabel, customer.dataExport,
  //        customer.escalation, customer.aegisPortal, customer.brandedDashboards, customer.apiAccess
  // platform.customer_*_active namespace is PROHIBITED.
  'customer',
  // PILLAR-KFIN-1 (2026-04-30) — Financial Pillar substrate (FROZEN)
  // Flags: kfin.financial_decisions, kfin.capital_pricing, kfin.cross_tenant_capital_pool,
  //        kfin.compliance_as_signal, kfin.margin_visibility, kfin.forecast, kfin.tenant_tier_pricing
  'kfin',
  // PILLAR-KSCHED-1 (2026-05-01) — Scheduling Pillar substrate (FROZEN)
  // Flags: ksched.crossSubstrateSchedule, ksched.constraintSolving, ksched.conflictDetection,
  //        ksched.scheduleTriggers, ksched.customerScheduleVisibility
  // CAP-TAX-1 BINDING: ksched-tier checks use config.modules.enabled.ksched.<feature>.
  'ksched',
  // PILLAR-KSALES-OPS-1 (2026-05-01) — Sales Operations Pillar R3 (FROZEN)
  // Flags: ksales-ops.analytics, ksales-ops.forecasting, ksales-ops.quota_management,
  //        ksales-ops.attribution, ksales-ops.dashboard
  'ksales-ops',
  // PILLAR-KSTRAT-1 (2026-05-01) — Strategy Pillar R3 (FROZEN)
  // Flags: kstrat.planning, kstrat.decisions, kstrat.intel, kstrat.narrative, kstrat.forecast
  'kstrat',
] as const;

export type CapabilityNamespace = (typeof CAPABILITY_NAMESPACES)[number];

/**
 * CapabilityFlag — discriminated template-literal union.
 * Any string shaped `<namespace>.<token>` where <namespace> is frozen.
 */
export type CapabilityFlag = `${CapabilityNamespace}.${string}`;

export type CapabilityTier =
  | 'comms'
  | 'cx'
  | 'cx_cloud'
  | 'enterprise'
  | 'dod_hardened'
  | 'custom';

export interface TenantCapabilities {
  tenantId: string;
  tier: CapabilityTier;
  flags: Record<CapabilityFlag, boolean>;
  signedBy: string;
  signedAt: string;
  version: number;
  softDeleted: boolean;
  softDeletedAt: string | null;
}

const NAMESPACE_UNION = CAPABILITY_NAMESPACES.join('|');

export const CAPABILITY_FLAG_PATTERN = new RegExp(
  `^(${NAMESPACE_UNION})\\.[A-Za-z0-9_]+(\\.[A-Za-z0-9_]+)*$`,
);

export const CapabilityFlagSchema = z
  .string()
  .regex(
    CAPABILITY_FLAG_PATTERN,
    'Flag must be <namespace>.<token> where namespace is in the frozen list',
  )
  .describe('CapabilityFlag');

export const CapabilityTierSchema = z
  .enum(['comms', 'cx', 'cx_cloud', 'enterprise', 'dod_hardened', 'custom'])
  .describe('CapabilityTier');

export const TenantCapabilitiesFlagsSchema = z
  .record(CapabilityFlagSchema, z.boolean())
  .describe('TenantCapabilitiesFlags');

export const TenantCapabilitiesSchema = z
  .object({
    tenantId: z.string().min(1),
    tier: CapabilityTierSchema,
    flags: TenantCapabilitiesFlagsSchema,
    signedBy: z.string().min(1),
    signedAt: z.string().min(1),
    version: z.number().int().nonnegative(),
    softDeleted: z.boolean(),
    softDeletedAt: z.string().nullable(),
  })
  .describe('TenantCapabilities');

export function isCapabilityFlag(value: unknown): value is CapabilityFlag {
  return typeof value === 'string' && CAPABILITY_FLAG_PATTERN.test(value);
}

export function namespaceOf(flag: CapabilityFlag): CapabilityNamespace {
  return flag.split('.', 1)[0] as CapabilityNamespace;
}
